import { useEffect, useRef } from "preact/hooks";
import type { DisplayState } from "../state.js";
import { DEFAULT_RESOLVED, type ResolvedOrbPalette } from "../orbPalette.js";

/**
 * Animated voice orb — a faithful port of the Foundry Voice Live playground
 * "harmonic" orb (canvas 2D). It draws layered harmonic waves clipped to a
 * Figma-exported gradient sphere, with per-state configs and smooth transitions.
 *
 * Ported from azure-ai-foundry's `VoiceOrbHarmonic` (canvas + SVG base disc).
 * Kept self-contained (no jotai / external state): it takes the widget's
 * `displayState` and reads audio reactivity from the inherited `--va-level`
 * CSS var (driven by mountVoiceAgent's setOutputLevel).
 */

type OrbState = "ready" | "connecting" | "listening" | "speaking";

/** Canvas backing resolution (drawn at ~2× the 132px display size for retina sharpness). */
const CANVAS_PX = 400;
const STATE_TRANSITION_MS = 900;
const AUDIO_SMOOTHING = 0.18;

/**
 * Base gradient disc — exported from Figma (node 13877:32056): a radial gradient
 * plus three linear-gradient overlays. The radial uses a gradientTransform matrix
 * Canvas can't reproduce via createRadialGradient, so we render the SVG natively.
 *
 * The four radial stops are the only palette-driven part; the overlay linears keep
 * their fixed light/shading tints so the sphere's form reads the same across hues.
 */
function buildBaseSvg(stops: ResolvedOrbPalette["base"]): string {
  return `<svg preserveAspectRatio="none" width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
<g id="Base">
<path d="M200 100C200 155.228 155.228 200 100 200C44.7715 200 0 155.228 0 100C0 44.7715 44.7715 0 100 0C155.228 0 200 44.7715 200 100Z" fill="url(#paint0_radial)"/>
<path d="M200 100C200 155.228 155.228 200 100 200C44.7715 200 0 155.228 0 100C0 44.7715 44.7715 0 100 0C155.228 0 200 44.7715 200 100Z" fill="url(#paint1_linear)"/>
<path d="M200 100C200 155.228 155.228 200 100 200C44.7715 200 0 155.228 0 100C0 44.7715 44.7715 0 100 0C155.228 0 200 44.7715 200 100Z" fill="url(#paint2_linear)" fill-opacity="0.2"/>
<path d="M200 100C200 155.228 155.228 200 100 200C44.7715 200 0 155.228 0 100C0 44.7715 44.7715 0 100 0C155.228 0 200 44.7715 200 100Z" fill="url(#paint3_linear)" fill-opacity="0.2"/>
</g>
<defs>
<radialGradient id="paint0_radial" cx="0" cy="0" r="1" gradientTransform="matrix(-228.841 172.528 -530.305 -686.24 186.372 33.3334)" gradientUnits="userSpaceOnUse">
<stop stop-color="${stops[0]}"/>
<stop offset="0.307745" stop-color="${stops[1]}"/>
<stop offset="0.620249" stop-color="${stops[2]}"/>
<stop offset="1" stop-color="${stops[3]}"/>
</radialGradient>
<linearGradient id="paint1_linear" x1="157.709" y1="49.3015" x2="8.27067" y2="142.099" gradientUnits="userSpaceOnUse">
<stop offset="0.000923382" stop-color="#F5F5F5"/>
<stop offset="0.687392" stop-color="${stops[1]}"/>
<stop offset="0.992779" stop-color="${stops[1]}"/>
</linearGradient>
<linearGradient id="paint2_linear" x1="123.681" y1="55.1414" x2="100" y2="200" gradientUnits="userSpaceOnUse">
<stop offset="0.0577277" stop-color="#F5F5F5"/>
<stop offset="0.591389" stop-color="${stops[2]}"/>
<stop offset="1" stop-color="${stops[1]}"/>
</linearGradient>
<linearGradient id="paint3_linear" x1="100.559" y1="81.0328" x2="149.097" y2="179.262" gradientUnits="userSpaceOnUse">
<stop stop-color="${stops[2]}"/>
<stop offset="1" stop-color="${stops[1]}"/>
</linearGradient>
</defs>
</svg>`;
}

interface WaveDef {
  readonly phase: number;
  readonly amp: number;
  readonly speed: number;
  readonly yOff: number;
}

/** Per-wave geometry (phase/amp/speed/offset). Colors come from the palette. */
const WAVE_GEOM: readonly WaveDef[] = [
  { phase: 0, amp: 1, speed: 0.8, yOff: 0.58 },
  { phase: 1.2, amp: 0.85, speed: 1.1, yOff: 0.52 },
  { phase: 2.5, amp: 0.7, speed: 0.6, yOff: 0.48 },
];

interface StateConfig {
  activeWaves: number;
  ampScale: number;
  speedScale: number;
  audioReactivity: number;
  pulseAmp: number;
  breathAmp: number;
}

const STATE_CONFIGS: Record<OrbState, StateConfig> = {
  ready: {
    activeWaves: 2,
    ampScale: 2,
    speedScale: 1.5,
    audioReactivity: 0,
    pulseAmp: 0,
    breathAmp: 0.06,
  },
  connecting: {
    activeWaves: 1,
    ampScale: 2.2,
    speedScale: 1.35,
    audioReactivity: 0.15,
    pulseAmp: 0.3,
    breathAmp: 0.05,
  },
  listening: {
    activeWaves: 2,
    ampScale: 2,
    speedScale: 1.95,
    audioReactivity: 0.5,
    pulseAmp: 0,
    breathAmp: 0.03,
  },
  speaking: {
    activeWaves: 3,
    ampScale: 2,
    speedScale: 2.2,
    audioReactivity: 1,
    pulseAmp: 0,
    breathAmp: 0.01,
  },
};

function easeInOutQuint(t: number): number {
  return t < 0.5 ? 16 * t * t * t * t * t : 1 - (-2 * t + 2) ** 5 / 2;
}

function lerpConfig(a: StateConfig, b: StateConfig, t: number): StateConfig {
  const l = (x: number, y: number): number => x + (y - x) * t;
  return {
    activeWaves: l(a.activeWaves, b.activeWaves),
    ampScale: l(a.ampScale, b.ampScale),
    speedScale: l(a.speedScale, b.speedScale),
    audioReactivity: l(a.audioReactivity, b.audioReactivity),
    pulseAmp: l(a.pulseAmp, b.pulseAmp),
    breathAmp: l(a.breathAmp, b.breathAmp),
  };
}

function toOrbState(s: DisplayState): OrbState {
  switch (s) {
    case "connecting":
      return "connecting";
    case "listening":
    case "thinking":
      return "listening";
    case "speaking":
      return "speaking";
    default:
      return "ready";
  }
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const now = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

export function Orb({
  displayState,
  palette = DEFAULT_RESOLVED,
}: {
  displayState: DisplayState;
  palette?: ResolvedOrbPalette;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<OrbState>("ready");
  const prevStateRef = useRef<OrbState>("ready");
  const transitionStartRef = useRef(0);
  const phaseRef = useRef<number[]>(WAVE_GEOM.map((w) => w.phase));
  const lastTimeRef = useRef<number | null>(null);
  const smoothAudioRef = useRef(0);
  const baseImgRef = useRef<HTMLImageElement | null>(null);
  const rafRef = useRef(0);
  // The draw loop is a long-lived rAF closure created once; it reads wave colors
  // from this ref so a palette change repaints without tearing down the loop.
  const paletteRef = useRef(palette);
  paletteRef.current = palette;
  // When the loop can't run (no requestAnimationFrame, e.g. SSR/tests), a single
  // static frame is drawn on mount — but the base disc image loads async and may
  // not be ready yet, so the disc would be missing. The image's load handler
  // calls this to repaint that one frame once the disc is available.
  const redrawStaticRef = useRef<(() => void) | null>(null);

  const orbState = toOrbState(displayState);

  useEffect(() => {
    if (orbState !== stateRef.current) {
      prevStateRef.current = stateRef.current;
      stateRef.current = orbState;
      transitionStartRef.current = now();
    }
  }, [orbState]);

  // Load the base gradient disc. Rebuilt + reloaded whenever the base stops
  // change so a palette swap recolors the sphere (the joined key keeps this to
  // actual color changes, not new-array identity on every render).
  const baseKey = palette.base.join("|");
  useEffect(() => {
    if (typeof Image === "undefined") return;
    const img = new Image();
    img.addEventListener("load", () => {
      baseImgRef.current = img;
      // If we're not animating, repaint the one static frame now that the disc
      // is loaded (otherwise the base gradient sphere never appears).
      redrawStaticRef.current?.();
    });
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(buildBaseSvg(palette.base))}`;
  }, [baseKey]);

  // Canvas draw loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function") return;
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext("2d");
    } catch {
      return;
    }
    if (!ctx) return;
    const c = ctx;

    // Bridge to the shell's audio-reactive signal: mountVoiceAgent drives a
    // `--va-level` (0..1) CSS var via setOutputLevel; it inherits to this canvas.
    // The CSSStyleDeclaration is live, so we resolve it once and re-read the var
    // each frame rather than calling getComputedStyle (a style flush) per frame.
    let computed: CSSStyleDeclaration | null = null;
    try {
      computed = getComputedStyle(canvas);
    } catch {
      computed = null;
    }
    const readLevel = (): number => {
      if (!computed) return 0;
      const n = Number.parseFloat(computed.getPropertyValue("--va-level"));
      return Number.isFinite(n) ? clamp01(n) : 0;
    };

    // The orb flows continuously (ambient, low-amplitude). Matches the Foundry
    // source, which animates unconditionally rather than gating on reduced-motion.
    const draw = (): void => {
      try {
        drawFrame();
      } finally {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    const drawFrame = (): void => {
      const W = CANVAS_PX;
      const H = CANVAS_PX;
      const t = now();
      const nowSec = t / 1000;
      const last = lastTimeRef.current;
      const dtRaw = last === null ? 1 / 60 : nowSec - last;
      const dt = Math.min(Math.max(dtRaw, 0), 0.05);
      lastTimeRef.current = nowSec;

      const active = stateRef.current === "speaking" || stateRef.current === "listening";
      const targetAudio = active ? readLevel() : 0;
      smoothAudioRef.current += (targetAudio - smoothAudioRef.current) * AUDIO_SMOOTHING;
      const audio = smoothAudioRef.current;

      const fromCfg = STATE_CONFIGS[prevStateRef.current];
      const toCfg = STATE_CONFIGS[stateRef.current];
      const progress = Math.min(1, (t - transitionStartRef.current) / STATE_TRANSITION_MS);
      const eased = easeInOutQuint(progress);
      const cfg = progress >= 1 ? toCfg : lerpConfig(fromCfg, toCfg, eased);

      const phases = phaseRef.current;
      WAVE_GEOM.forEach((wave, wi) => {
        phases[wi] = (phases[wi] ?? wave.phase) + dt * wave.speed * cfg.speedScale;
      });

      c.clearRect(0, 0, W, H);
      if (baseImgRef.current) c.drawImage(baseImgRef.current, 0, 0, W, H);

      c.save();
      c.beginPath();
      c.arc(W / 2, H / 2, W / 2, 0, Math.PI * 2);
      c.clip();

      const pulse = cfg.pulseAmp * Math.sin(nowSec * Math.PI * 2 * 1.2);
      const breath = cfg.breathAmp * Math.sin(nowSec * Math.PI * 2 * 0.25);

      const waveColors = paletteRef.current.waves;
      WAVE_GEOM.forEach((wave, wi) => {
        const fromVis = clamp01(fromCfg.activeWaves - wi);
        const toVis = clamp01(toCfg.activeWaves - wi);
        const visibility = fromVis + (toVis - fromVis) * eased;
        if (visibility <= 0.001) return;

        const baseAmp =
          (60 + audio * cfg.audioReactivity * 120) * wave.amp * cfg.ampScale +
          pulse * 40 +
          breath * 30;
        const baseY = H * wave.yOff - audio * cfg.audioReactivity * 50;
        const phase = phases[wi] ?? wave.phase;

        const points: { x: number; y: number }[] = [];
        for (let x = 0; x <= W; x++) {
          const xn = x / W;
          const skew = xn ** 1.2 * (1 - xn) ** 2;
          const env = skew / 0.21;
          const y =
            baseY +
            Math.sin(xn * Math.PI * 0.9 + phase) * baseAmp * env +
            Math.sin(xn * Math.PI * 2.2 + phase * 1.3 + wave.phase * 0.7) * baseAmp * 0.12 * env +
            Math.sin(xn * Math.PI * 3.5 + phase * 0.7 + wave.phase * 1.2) * baseAmp * 0.06 * env;
          points.push({ x, y });
        }

        c.save();
        c.globalAlpha = visibility;

        c.beginPath();
        c.moveTo(0, H);
        for (const p of points) c.lineTo(p.x, p.y);
        c.lineTo(W, H);
        c.closePath();

        const grad = c.createLinearGradient(0, baseY - baseAmp, 0, H);
        const colors = waveColors[wi] ?? waveColors[0];
        grad.addColorStop(0, colors[0]);
        grad.addColorStop(1, colors[1]);
        c.fillStyle = grad;
        c.fill();

        c.shadowColor = colors[0];
        c.shadowBlur = 14;
        c.strokeStyle = colors[0];
        c.lineWidth = 3;
        c.beginPath();
        points.forEach((p, i) => (i === 0 ? c.moveTo(p.x, p.y) : c.lineTo(p.x, p.y)));
        c.stroke();

        c.shadowBlur = 0;
        c.strokeStyle = "rgba(255, 255, 255, 0.3)";
        c.lineWidth = 1;
        c.beginPath();
        points.forEach((p, i) => (i === 0 ? c.moveTo(p.x, p.y) : c.lineTo(p.x, p.y)));
        c.stroke();

        c.restore();
      });

      c.restore();
    };

    if (typeof requestAnimationFrame === "undefined") {
      drawFrame();
      redrawStaticRef.current = drawFrame;
      return () => {
        redrawStaticRef.current = null;
      };
    }
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <span
      class="va-orb"
      data-testid="va-indicator"
      data-indicator={displayState}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} class="va-orb-canvas" width={CANVAS_PX} height={CANVAS_PX} />
    </span>
  );
}
